import { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonSpinner,
  IonBadge
} from '@ionic/react';
import { bookOutline, checkmarkCircle, trophy, documentText, timeOutline, close, downloadOutline } from 'ionicons/icons';
import { Activity } from '../../types';
import { TeamMember } from '../../types/teamSummary';
import { fetchActivityData } from '../../utils/activityData';
import { exportActivityLogToCsv } from '../../utils/exportActivityLog';
import { format, isToday, isYesterday, parseISO, formatDistanceToNow } from 'date-fns';
import { useIsMobile } from '../../hooks/useMediaQuery';

interface ActivityLogModalProps {
  member: TeamMember;
  isOpen: boolean;
  onClose: () => void;
}

export function ActivityLogModal({ member, isOpen, onClose }: ActivityLogModalProps) {
  const isMobile = useIsMobile();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && member) {
      setLoading(true);
      setError(null);
      setActivities([]);

      fetchActivityData(member)
        .then((data) => {
          setActivities(data);
          setLoading(false);
        })
        .catch((err) => {
          // Error modal presented by API
          setLoading(false);
        });
    }
  }, [isOpen, member]);

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'course_started':
        return bookOutline;
      case 'course_completed':
        return checkmarkCircle;
      case 'badge_earned':
        return trophy;
      case 'certification_completed':
        return documentText;
      case 'time_active':
        return timeOutline;
      default:
        return bookOutline;
    }
  };

  const getActivityBadgeColor = (type: Activity['type']): 'primary' | 'success' | 'warning' | 'tertiary' | 'secondary' => {
    switch (type) {
      case 'course_started':
        return 'primary';
      case 'course_completed':
        return 'success';
      case 'badge_earned':
        return 'warning';
      case 'certification_completed':
        return 'tertiary';
      case 'time_active':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  const formatActivityDate = (timestamp: string): string => {
    const date = parseISO(timestamp);
    if (isToday(date)) {
      return 'Today';
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM dd, yyyy');
    }
  };

  const formatActivityTime = (timestamp: string): string => {
    return format(parseISO(timestamp), 'HH:mm');
  };

  const formatRelativeTime = (timestamp: string): string => {
    return formatDistanceToNow(parseISO(timestamp), { addSuffix: true });
  };

  // Group activities by date
  const groupedActivities = activities.reduce((groups, activity) => {
    const date = formatActivityDate(activity.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(activity);
    return groups;
  }, {} as Record<string, Activity[]>);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className={isMobile ? 'modal-mobile' : 'modal-desktop'}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{member.displayName}'s Activity Log</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" className="toolbar-button" onClick={onClose}>
              <IonIcon icon={close} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <div className="px-4 pb-2">
          <p>Last 30 days</p>
        </div>
      </IonHeader>
      <IonContent className="ion-padding">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <IonSpinner name="crescent" />
              <p>Loading activities...</p>
            </div>
          )}

          {error && (
            <div>
              {error}
            </div>
          )}

          {!loading && !error && activities.length === 0 && (
            <div className="py-12">
              <IonIcon icon={documentText} />
              <p>No activities found</p>
              <p>This user has no activity in the last 30 days</p>
            </div>
          )}

          {!loading && !error && activities.length > 0 && (
            <div>
              {Object.entries(groupedActivities).map(([date, dateActivities]) => (
                <div key={date}>
                  <div className="py-2">
                    <div>{date}</div>
                  </div>
                  
                  <div className="pl-4 border-l">
                    {dateActivities.map((activity) => (
                      <div 
                        key={activity.id}
                        className="py-2 flex gap-4"
                      >
                        <IonIcon 
                          icon={getActivityIcon(activity.type)} 
                          style={{ color: 'var(--ion-color-dark)' }}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between gap-4">
                            <IonBadge color={getActivityBadgeColor(activity.type)}>
                              {activity.title}
                            </IonBadge>
                            <span>
                              {formatActivityTime(activity.timestamp)}
                            </span>
                          </div>
                          <p>
                            {activity.description}
                          </p>
                          <p>
                            {formatRelativeTime(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
      </IonContent>
      <div className="px-4 py-3 border-t flex items-center justify-between">
        <p>
          {activities.length} {activities.length === 1 ? 'activity' : 'activities'} total
        </p>
        <div className="flex items-center gap-4">
          <IonButton
            onClick={() => exportActivityLogToCsv(activities, member.displayName)}
            fill="outline"
            color="primary"
            disabled={activities.length === 0 || loading}
          >
            <IonIcon icon={downloadOutline} slot="start" />
            Export CSV
          </IonButton>
          <IonButton
            onClick={onClose}
            color="primary"
          >
            Close
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}

